import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MessageSquareText, Star, CheckCircle, Flag, Download, Loader2 } from 'lucide-react';
import { ReviewsManagementTable, type ReviewFilters } from '@/components/reviews/admin';
import { reviewService } from '@/services/review.service';
import { exportService } from '@/services/export.service';
import { toast } from '@/components/ui/Toast';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { REVIEWS_PERMISSION } from '@/constants/constant-permissions';

export default function ReviewsManagement() {
  usePermissionRouteAccess([
    REVIEWS_PERMISSION.ADMIN_REVIEWS_MANAGEMENT_READ,
    REVIEWS_PERMISSION.ADMIN_REVIEWS_MANAGEMENT_WRITE,
  ]);
  const canWrite = useHasPermission([REVIEWS_PERMISSION.ADMIN_REVIEWS_MANAGEMENT_WRITE]);
  const [isExporting, setIsExporting] = useState(false);
  const filtersRef = useRef<ReviewFilters>({});

  // Fetch admin dashboard metrics
  const { data: metrics } = useQuery({
    queryKey: ['reviews', 'admin', 'metrics'],
    queryFn: () => reviewService.getAdminDashboardMetrics(),
  });

  // Handle filter changes from table
  const handleFiltersChange = (filters: ReviewFilters) => {
    filtersRef.current = filters;
  };

  // Export reviews with current filters
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const filters = filtersRef.current;
      const result = await exportService.exportReviews('excel', {
        review_type: filters.review_type || undefined,
        status: filters.status || undefined,
        rating: filters.rating ? parseInt(filters.rating) : undefined,
        search: filters.search || undefined,
      });
      if (result.success) {
        toast.success(`Exported ${result.count} reviews successfully`);
      } else {
        toast.error(result.message || 'No reviews available to export');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export reviews');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-700 rounded-2xl shadow-xl">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-violet-400/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-300/10 rounded-full blur-3xl" />
        </div>

        {/* Header Content */}
        <div className="relative px-6 pt-6 pb-8 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-lg ring-1 ring-white/30">
                <MessageSquareText className="w-8 h-8 text-white drop-shadow-md" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight drop-shadow-sm">
                  Reviews Management
                </h1>
                <p className="text-purple-100/90 mt-1 text-sm sm:text-base">
                  Moderate and manage learner course reviews
                </p>
              </div>
            </div>

            {/* Export Button */}
            <button
              onClick={() => canWrite && handleExport()}
              disabled={!canWrite || isExporting}
              title={!canWrite ? 'You do not have permission' : undefined}
              className="flex items-center gap-2 px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span className="hidden sm:inline">
                {isExporting ? 'Exporting...' : 'Export'}
              </span>
            </button>
          </div>

          {/* Metrics Boxes */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-white">
                  {metrics?.total_reviews ?? 0}
                </p>
                <p className="text-sm text-white/80 mt-1">Total</p>
              </div>
              <Star className="w-10 h-10 text-white/30" />
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-white">
                  {metrics?.approved_count ?? 0}
                </p>
                <p className="text-sm text-white/80 mt-1">Approved</p>
              </div>
              <CheckCircle className="w-10 h-10 text-white/30" />
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-white">
                  {metrics?.flagged_count ?? 0}
                </p>
                <p className="text-sm text-white/80 mt-1">Flagged</p>
              </div>
              <Flag className="w-10 h-10 text-white/30" />
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Table */}
      <ReviewsManagementTable onFiltersChange={handleFiltersChange} canWrite={canWrite} />
    </div>
  );
}
